import { Box, Button, Grid, useTheme } from "@mui/material";
import * as React from "react";
import AlertCard from "../../../components/alerting/alert_card";
import useBackend, { useAllowedFeatures, useUserPrivilegeKeyPermissionsMap } from "../../../hooks/use_backend";
import Loader from "../../../components/general/loader";
import { useParams } from "react-router-dom";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { useQuery } from "@tanstack/react-query";
import { GeneralInfoCard } from "../../../components/general_tab/general_info_card";
import { fetchSiteData } from "../../../scripts/api_v2/site_client";
import { ProductCard } from "../../../components/general_tab/product_card";
import { OverridesCard } from "../../../components/general_tab/overrides_card";
import { NotesCard } from "../../../components/general_tab/notes_card";
import ActivityCard from "../../../components/general_tab/activity_card";
import Parameters from "../../../components/general/parameters";
import ClientCard from "../../../components/general_tab/client_card";
import ContractCard from "../../../components/general_tab/contract_card";
import { isComponentDisabledForUpdates } from "../../login/login_utils";
import { CommandCenter } from "../../../components/general_tab/command_center";
import { useOverrides } from "../../../hooks/use_overrides";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";
import { ControlLoopCard } from "components/general_tab/control_loop/control_loop_card";
import Privileges from "../../../enums/privilege_enum";
import { useDeactivateSiteMutation } from "../../../hooks/use_site";

export default function GeneralTab({ required_privilege }) {
  const params = useParams();
  const backend = useBackend();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const allowedFeatures = useAllowedFeatures();
  const isAdmin = allowedFeatures.includes(Privileges.ADMIN_OVERVIEW.feature);

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const { data: overrides, errorOverrides, isLoadingOverides } = useOverrides();
  const deactivateSiteMutation = useDeactivateSiteMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const canEditOverrides = !isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());

  if (isLoading) {
    return <Loader fill={false} />;
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>Error loading data</div>
      </Box>
    );
  }

  if (!siteData) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      <Grid item md={4} xs={12} display="flex" flexDirection="column">
        <GeneralInfoCard required_privilege={required_privilege} />
        <AlertCard tooltip={TOOLTIPS_EN.ALERTS_CARD} sx={{ mt: 2 }} />
        <ContractCard sx={{ mt: 2 }} contractType="offtake" startCollapsed />
        <ContractCard sx={{ mt: 2 }} contractType="injection" startCollapsed />
        {
          // <ControlCard sx={{mt: 2}}/>
        }
        <ClientCard tooltip={TOOLTIPS_EN.BOX_INFO_CARD} sx={{ mt: 2 }} required_privilege={required_privilege} />

        <CommandCenter
          tooltip={TOOLTIPS_EN.COMMANDS_CARD}
          sx={{ mt: 2 }}
          permissionMap={permissionMap}
          required_privilege={required_privilege}
        />

        {isAdmin && (
          <Button
            variant="contained"
            color="inherit"
            size="small"
            sx={{ mt: 2, borderRadius: 2 }}
            onClick={() => deactivateSiteMutation.mutate({ siteId: params.site_id })}
            disabled={deactivateSiteMutation.isPending || !siteData?.is_active}
          >
            {deactivateSiteMutation.isPending ? "Deactivating..." : "Deactivate Site"}
          </Button>
        )}
      </Grid>

      <Grid item md={8} xs={12}>
        <ProductCard required_privilege={required_privilege} />
        <Parameters sx={{ mt: 2 }} />
        <OverridesCard
          tooltip={TOOLTIPS_EN.OVERRIDES_CARD}
          overrides={overrides}
          isLoading={isLoadingOverides}
          error={errorOverrides}
          sx={{ mt: 2 }}
          canEdit={canEditOverrides}
        />
        <ActivityCard tooltip={TOOLTIPS_EN.ACTIVITY_CARD} sx={{ mt: 2 }} /> {/* Site activity */}
        <NotesCard sx={{ mt: 2 }} permissionMap={permissionMap} required_privilege={required_privilege} />{" "}
        {/* Site notes */}
      </Grid>
    </Grid>
  );
}
